import { DateUtils } from '@web_opxp/features';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import { ISessionState, statePropName } from './slice';
import { calcNextDifficulty, getMaxReachDifficultyOfSiblings } from './utils';

export interface IAppState {
  // this feature state
  [statePropName]: ISessionState;
  // other features we don't care of
  [otherKeys: string]: any;
}

export const getState = (state: IAppState) => state[statePropName];

export const isLoading = (state: IAppState) => getState(state).loading;

export const getSessionDomain = (state: IAppState) => getState(state).domain;

export const getSessionWorkouts = (state: IAppState) => getState(state).session;

export const getWorkoutIdByRelativeIndex = (state: IAppState, index: number) => getSessionWorkouts(state)[index];

const getWorkoutStats = (state: IAppState, workoutId: number) => getState(state).sessionWorkouts[workoutId];

export const statsLoaded = (state: IAppState, workoutId: number) => !!getWorkoutStats(state, workoutId);

export const getWorkoutComparison = (state: IAppState, workoutId: number) => {
  const stats = getWorkoutStats(state, workoutId);
  if (!stats) {
    return null;
  }
  return stats.comparison;
};

export const isWorkoutLocked = (state: IAppState, workoutId: number) => {
  const stats = getWorkoutStats(state, workoutId);
  if (!stats) {
    return null;
  }
  return stats.locked;
};

export const getExerciseName = (state: IAppState, workoutId: number) => {
  const stats = getWorkoutStats(state, workoutId);
  if (!stats) {
    return null;
  }
  return stats.exerciseName;
};

export const getCurrentWorkoutGoal = (state: IAppState, workoutId: number) => {
  const stats = getWorkoutStats(state, workoutId);
  if (!stats) {
    return null;
  }
  return stats.goal;
};

export const getCurrentWorkoutSiblings = (state: IAppState, workoutId: number) => {
  const stats = getWorkoutStats(state, workoutId);
  if (!stats) {
    return [];
  }
  return stats.siblings;
};

const getWorkoutStates = (state: IAppState) => getState(state).workoutStates;

export const getWorkoutState = (state: IAppState, workoutId: number) =>
  find(getWorkoutStates(state), ['workoutId', workoutId]);

export const getFilteredWorkoutStates = (state: IAppState, workoutIds: Array<number>) =>
  getWorkoutStates(state).filter((workoutState) => workoutIds.includes(workoutState.workoutId));

export const getWorkoutGoal = (state: IAppState, workoutId: number) => {
  const workoutstate = getWorkoutState(state, workoutId);
  return workoutstate ? workoutstate.goal : null;
};

export const getReachedDifficulty = (state: IAppState, workoutId: number) => {
  const workoutstate = getWorkoutState(state, workoutId);
  return workoutstate ? workoutstate.reachDifficulty : null;
};

// narrow typing of progress data needed for difficulty selection
interface IProgressEntry {
  timestamp: number;
  endDifficulty: number;
}

/**
 * selector to get next start difficulty calculated from current finished difficulty and workouts states
 * @param state application state
 * @param workoutIds workout ids
 * @param lastProgress progress data of the last gameplay including siblings
 */
export const getNextDifficultyForSiblings = <T extends IProgressEntry>(
  state: IAppState,
  workoutIds: Array<number>,
  lastProgress?: T
) => {
  const workoutstates = getFilteredWorkoutStates(state, workoutIds);
  // Find higher reachDifficulty from all provided workouts
  const stateDifficulty = getMaxReachDifficultyOfSiblings(workoutstates);

  // if there were no gameplays before - take last finished difficulty from progress
  const progressDifficulty = lastProgress ? lastProgress.endDifficulty : 1;
  // if there were no gameplays - consider last gameplay is now
  const timestamp =
    lastProgress && lastProgress.timestamp !== -1 ? lastProgress.timestamp : DateUtils.getUnixTimestamp();
  return calcNextDifficulty(progressDifficulty, stateDifficulty, timestamp);
};

export const getCompletedState = (state: IAppState) => getState(state).completed;

export const isWorkoutCompleted = (state: IAppState, index: number) => getCompletedState(state)[index];

export const getDisabledState = (state: IAppState) => getState(state).disabled;

export const isWorkoutDisabled = (state: IAppState, index: number) => getDisabledState(state)[index];

export const isWorkoutDisabledById = (state: IAppState, workoutId: number) => {
  const session = getSessionWorkouts(state);
  const disabled = getDisabledState(state);
  const workoutIndex = session.indexOf(workoutId);
  if (workoutIndex < 0) {
    return false;
  }
  return disabled[workoutIndex];
};

export const getActiveWorkoutRelativeIndex = (state: IAppState) => {
  // find next not completed and not disabled workout
  const session = getSessionWorkouts(state);
  const completed = getCompletedState(state);
  const disabled = getDisabledState(state);
  return findIndex(session, (w: number, i: number) => {
    return !completed[i] && !disabled[i];
  });
};

export const getActiveWorkoutId = (state: IAppState) => {
  const index = getActiveWorkoutRelativeIndex(state);
  if (index === -1) {
    return 0;
  }
  const session = getSessionWorkouts(state);
  return session[index];
};

export const getLastCompletedWorkoutIndex = (state: IAppState) => {
  // find last completed index
  const session = getSessionWorkouts(state);
  const completed = getCompletedState(state);

  return findLastIndex(session, (w: number, i: number) => {
    return completed[i];
  });
};

export const getLastCompletedWorkoutId = (state: IAppState) => {
  const index = getLastCompletedWorkoutIndex(state);

  if (index === -1) {
    return 0;
  }

  const session = getSessionWorkouts(state);

  return session[index];
};

export const getNextWorkoutIndex = (state: IAppState) => {
  const sessionLength = getSessionWorkouts(state).length;
  const activeIndex = getActiveWorkoutRelativeIndex(state);
  if (activeIndex === -1) {
    return activeIndex;
  }
  const disabled = getDisabledState(state);
  for (let nextIndex = activeIndex + 1; nextIndex < sessionLength; nextIndex++) {
    if (!disabled[nextIndex]) {
      return nextIndex;
    }
  }
  return -1;
};

export const getNextWorkoutId = (state: IAppState) => {
  const index = getNextWorkoutIndex(state);
  if (index === -1) {
    return 0;
  }
  return getWorkoutIdByRelativeIndex(state, index);
};

export const getWorkoutExerciseIdForLink = (state: IAppState, workoutId: number) => {
  const exerciseId = getExerciseName(state, workoutId);
  return exerciseId ? exerciseId.toLowerCase() : '';
};

export const allExceptCurrentWorkoutsCompleted = (state: IAppState) => {
  const activeWorkoutId = getActiveWorkoutId(state);
  const workouts = getSessionWorkouts(state);
  return workouts.every(
    (id, index) => id === activeWorkoutId || isWorkoutCompleted(state, index) || isWorkoutDisabled(state, index)
  );
};

export const allWorkoutsCompleted = (state: IAppState) => {
  const activeIndex = getActiveWorkoutRelativeIndex(state);
  return activeIndex === -1;
};

export const isSessionStart = (state: IAppState) => {
  const workoutIndex = getActiveWorkoutRelativeIndex(state);
  const disabled = getDisabledState(state);
  // find first non disabled workout index
  const disabledIndex = findIndex(disabled, (isDisabled) => !isDisabled);
  // session is started when current workout is first enabled workout
  return workoutIndex === disabledIndex;
};

export const getSessionWorkoutsForUniqueExercises = (state: IAppState) => {
  const allWorkouts = getSessionWorkouts(state);
  const reversedWorkouts = allWorkouts.slice().reverse();
  const exerciseNames: string[] = [];
  const filteredWorkouts: number[] = reversedWorkouts.filter((workoutId) => {
    const exerciseName = getExerciseName(state, workoutId) ?? '';
    if (exerciseNames.includes(exerciseName)) {
      return false;
    }
    exerciseNames.push(exerciseName);
    return true;
  });
  return filteredWorkouts.reverse();
};
